import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { Button, Form, Modal, Spin } from "antd";
import { IDocumentFields } from "../../../../interfaces/ICase";
import {
  errorMessage,
  successMessage,
} from "../../../../utils/notificationUtils";
import {
  deleteUploadedDocument,
  downloadDocument,
  getAllStarhealthDocumentsData,
  getRequiredDocumentsByMaster,
  updateAllDocumentsData,
} from "../../../../apis/patientService";
import {
  deleteProcedureEchs,
  getConsultation,
  getDigitallySignedDocument,
  getImplants,
  getPreAuthClaimECHS,
  getRoomCharges,
  submitApprovalEchs,
} from "../../../../apis/SchemeService/ECHS/echsService";
import { message } from "antd";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import {
  getBasicDetails,
  getDeficiencyQuestionData,
  getTimelineDetails,
} from "../../../../models/patientSlice";
import DocumentDeficiencyModal from "../../../../components/patient/DocumentDeficiencyModal";
import FilePreviewModal from "../../../../components/patient/PreAuth-query/FilePreviewModal";
import PatientDetailsStageDocument from "../../../../components/modals/PatientDetailsStageDocument";
import dayjs from "dayjs";
import { fetchStepperData } from "../../../../models/preAuthSlice";
import ConfirmSubmissionModalStage from "../../../../components/modals/ConfirmSubmissionModalStage";
import {
  PreAuthClaimResponse,
  IClaimSubmissionResponse,
  IClaimRes,
  PreAuthClaimSection,
} from "../../../../interfaces/IPreAuthClaim";
import { StatusCodes } from "http-status-codes";
import EchsClaimForm from "../../../../components/patient/Claim/Echs/EchsClaimForm";
import {
  fetchEchsProcedure,
  fetchProcedureNew,
  intimateClaimSubmissionEchs,
  submitClaimSubmissionEchs,
  submitClaimSubmissionToEdgeProEchs,
} from "../../../../models/preAuthClaimSlice";
import MultipleFilesPreviewModal from "../../../../components/patient/PreAuth-query/MultipleFilesPreviewModal";
import BillAmountConfirmationModal from "../CGHS/BillAmountConfirmationModal";
import Loader from "../../../../utils/Spinners";
import { Typography } from "antd";
import { CheckCircleTwoTone, LoadingOutlined } from "@ant-design/icons";
import { Space } from "antd";
import { GovtBillFetchRequest } from "../../../../models/billsSlice";
import { getGovtBillList } from "../../../../apis/govtBillingService";
import { API_URL } from "../../../../utils/constants";
import { errorMessageModal } from "../../../../utils/modalMessage";
import {
  BillItemChecklistEntry,
  BillRequiredItem,
  ClaimExtraDocument,
} from "../../../../components/modals/BillRequiredDocumentModal";
import SupportingDocumentViewerModal from "../../../../components/modals/SupportingDocumentViewerModal";

interface Document {
  id: number;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  labelId?: string | number;
  documentName?: string;
  requestDocument?: boolean;
  responseDocument?: boolean;
  isBillRequiredDocument?: boolean;
  itemNames?: string[];
  items?: BillRequiredItem[];
  itemNameEntries?: BillItemChecklistEntry[];
  required?: boolean;
  multiple?: boolean;
  documentList?: any[];
  data?: string | null;
}

type ApiClaimExtraDocument = {
  id?: string;
  label?: string;
  labelId?: string | number;
  multiple?: boolean;
  data?: string | null;
  documentList?: Array<{
    documentType?: string;
    documentName?: string;
    documentUrl?: string;
    id?: number;
    documentLabelType?: string;
    itemNames?: BillItemChecklistEntry[];
  }>;
  itemNames?: BillItemChecklistEntry[];
};

type BillRequiredDocumentApi = {
  id: number;
  documentName: string;
  items?: BillRequiredItem[];
  /** @deprecated Use `items` — kept for older API responses */
  itemName?: string[];
};

interface DocumentUrl {
  documentType: string;
  documentName: string;
  documentUrl: string;
  id: number;
  referenceType: string | null;
  documentLabelType: any;
  deficiencyCheckStatus: any;
  remarks: string | null;
}

interface DocumentResponse {
  status: string;
  message?: string;
  data?: {
    documentUrl: string;
    id: number;
  };
}

interface DocumentUploadResponse extends DocumentUrl {}

interface BasicDetailsPayload {
  caseId: number;
  visitId: number;
}

type ClaimEchsContainerProps = {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

const clinicalConditions = [
  {
    key: "bp",
    label: "BLOOD PRESSURE",
    valuePrefix: "1",
  },
  {
    key: "cvs",
    label: "CARDIOVASCULAR SYSTEM",
    valuePrefix: "2",
  },
  {
    key: "rs",
    label: "RESPIRATORY SYSTEM",
    valuePrefix: "3",
  },
  {
    key: "abdomen",
    label: "ABDOMEN",
    valuePrefix: "4",
  },
  {
    key: "cns",
    label: "CENTRAL NERVOUS SYSTEM",
    valuePrefix: "5",
  },
  {
    key: "covid",
    label: "COVID POSITIVE",
    valuePrefix: "6",
  },
  {
    key: "clinicalOther",
    label: "OTHER",
    valuePrefix: "99",
  },
];

const treatmentConditions = [
  {
    key: "investigations",
    label: "INVESTIGATIONS",
    valuePrefix: "1",
  },
  {
    key: "intensiveCare",
    label: "INTENSIVE CARE",
    valuePrefix: "2",
  },
  {
    key: "medicalManagement",
    label: "MEDICAL MANAGEMENT",
    valuePrefix: "3",
  },
  {
    key: "surgicalManagement",
    label: "SURGICAL MANAGEMENT",
    valuePrefix: "4",
  },
  {
    key: "minorDescription",
    label: "PROCEDURE MINOR DESCRIPTION",
    valuePrefix: "5",
  },
  {
    key: "majorDescription",
    label: "PROCEDURE MAJOR DESCRIPTION",
    valuePrefix: "6",
  },
  {
    key: "proposedOther",
    label: "OTHER TREATMENT/PROCEDURE",
    valuePrefix: "99",
  },
];

const { Text, Title } = Typography;

const ClaimEchsContainer: FC<ClaimEchsContainerProps> = ({
  activeKey,
  sseData,
  sseUpdateCount,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, auth, enhancement, preAuth, preAuthClaim } = useSelector(
    (state: RootState) => state,
  );
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [isApprovedInitiated, setIsApprovedInitiated] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [documentTypes, setDocumentTypes] = useState<Document[]>([]);
  const [billSummaryDocument, setBillSummaryDocument] = useState<any>(null);
  const [auditReportDocument, setAuditReportDocument] = useState<any>(null);
  const [isIntimating, setIsIntimating] = useState<boolean>(false);
  const [fetchingDeficiencyData, setFetchingDeficiencyData] =
    useState<boolean>(false);
  const [
    documentRequestResponseDocuments,
    setDocumentRequestResponseDocuments,
  ] = useState<any>();
  const [roomChargesOptions, setRoomChargesOptions] = useState<any>([]);
  const [consultationChargesOptions, setConsultationChargesOptions] =
    useState<any>([]);
  const [implantListOptions, setImplantListOptions] = useState<any>([]);

  const [isClaimLoading, setIsClaimLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const [caseClaimId, setCaseClaimId] = useState<number | null>(null);
  const [documentUrls, setDocumentUrls] = useState<
    {
      documentType: string;
      documentName: string;
      documentUrl: string;
      id: number;
      referenceType: string | null;
      documentLabelType: any;
      deficiencyCheckStatus: any;
      remarks: string | null;
    }[]
  >([]);
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [claimData, setClaimData] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [portalSubmission, setPortalSubmission] = useState<boolean>(false);
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] =
    useState<any>();
  const [deleteProcedureList, setDeleteProcedureList] = useState<any[]>([]);

  const [selectedFiles, setSelectedFiles] = useState<
    { file: File; documentType: string }[]
  >([]);
  const [previewFiles, setPreviewFiles] = useState<
    {
      url: string;
      name: string;
      showUpload: boolean;
      documentType: string;
      isMultiple: boolean;
    }[]
  >([]);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);
  const [claimExtraDocuments, setClaimExtraDocuments] = useState<
    ClaimExtraDocument[]
  >([]);
  const [billRequiredDocuments, setBillRequiredDocuments] = useState<
    Document[]
  >([]);
  const [billRequiredUploadContext, setBillRequiredUploadContext] = useState<{
    doc: Document;
    checkedItems: BillItemChecklistEntry[];
  } | null>(null);
  const [billRequiredPreviewVisible, setBillRequiredPreviewVisible] =
    useState(false);
  const [billRequiredPreviewFile, setBillRequiredPreviewFile] = useState<{
    url: string;
    name: string;
    file: File | null;
  }>({
    url: "",
    name: "",
    file: null,
  });
  const [billRequiredUploading, setBillRequiredUploading] =
    useState<boolean>(false);
  const [supportingPreviewOpen, setSupportingPreviewOpen] =
    useState<boolean>(false);
  const [supportingClickedActiveTab, setSupportingClickedActiveTab] =
    useState<string>();
  const [
    supportingClickedActiveDocumentUrl,
    setSupportingClickedActiveDocumentUrl,
  ] = useState<string>();

  const [showUnsignedDocumentsModal, setShowUnsignedDocumentsModal] =
    useState<boolean>(false);
  const [unsignedDocumentsCount, setUnsignedDocumentsCount] =
    useState<number>(0);
  const [digitallySignedDocuments, setDigitallySignedDocuments] = useState<any>(
    [],
  );
  const [isFetchingDigitallySignedDocs, setIsFetchingDigitallySignedDocs] =
    useState<boolean>(false);
  const [isAllDigitallySignedModal, setIsAllDigitallySignedModal] =
    useState<boolean>(false);

  const [isErrorState, setIsErrorState] = useState<boolean>(false);
  const lastErrorMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (isClaimTabActive()) {
      lastErrorMessageRef.current = null;
    }
  }, [activeKey]);

  useEffect(() => {
    if (!previewModalVisible) {
      setSelectedFiles([]);
    }
  }, [previewModalVisible]);

  const isClaimTabActive = () => {
    if (!activeKey || activeKey.length === 0) return false;

    const currentStageId =
      typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]);

    const currentStage = preAuth?.stepperData?.visitStages?.find(
      (stage: any) => stage?.visitStageId === currentStageId,
    );

    setIsErrorState(currentStage?.state?.toLowerCase() === "error");

    return currentStage?.visitStage?.toUpperCase() === "CLAIM";
  };

  const currentActiveStage = preAuth?.stepperData?.visitStages?.find(
    (stage: any) =>
      stage?.visitStageId ===
      (typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0])),
  );

  useEffect(() => {
    if (
      isErrorState &&
      claimData?.caseClaimInitiateError &&
      claimData?.caseClaimInitiateError !== lastErrorMessageRef.current &&
      isClaimTabActive()
    ) {
      errorMessageModal(claimData?.caseClaimInitiateError);
      lastErrorMessageRef.current = claimData?.caseClaimInitiateError;
    }
  }, [claimData, isErrorState, activeKey]);

  useEffect(() => {
    if (claimData) {
      setPortalSubmission(claimData?.submitted);
    }
  }, [claimData]);

  const schemeMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;

  useEffect(() => {
    const listOfUploadedDocuments = documentTypes?.flatMap(
      (item: any) =>
        item?.documentList?.map((doc: any) => ({
          id: doc?.id,
          documentName: doc?.documentName,
          documentType: doc?.documentType,
          documentUrl: doc?.documentUrl,
          documentLabelType: doc?.documentLabelType,
          deficiencyCheckStatus: doc?.deficiencyCheckStatus,
        })) || [],
    );

    // let updatedUrls = listOfUploadedDocuments;
    // if (billSummaryDocument) {
    //   const hasBillSummary = updatedUrls?.some(
    //     (doc) => doc?.documentLabelType === "BILL SUMMARY",
    //   );
    //   if (!hasBillSummary) {
    //     updatedUrls = [...updatedUrls, billSummaryDocument];
    //   }
    // }
    // setDocumentUrls(updatedUrls);

    setDocumentUrls(listOfUploadedDocuments);
  }, [documentTypes]); //, billSummaryDocument

  const fetchGovtBills = async () => {
    try {
      if (
        !patients?.fetchBasicDetails?.casesResponse?.healthCareId ||
        !patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId
      )
        return;
      const response = await dispatch(
        GovtBillFetchRequest({
          healthCareId:
            patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          govtBillMappingId:
            patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId,
        }),
      );
      // setBillSummaryLink(
      //   response?.payload?.data?.billHeaderResponse?.billSummaryLink
      //     ? `${API_URL}${response?.payload?.data?.billHeaderResponse?.billSummaryLink}`
      //     : null,
      // );
      if (response?.payload?.data?.billHeaderResponse?.billSummaryLink) {
        const documentTypeBillSummary = documentTypes?.find(
          (doc) => doc?.label?.toUpperCase() === "BILL SUMMARY",
        );

        const billSummary = {
          id: null,
          documentName: "BILL SUMMARY",
          documentType: documentTypeBillSummary?.labelId
            ? documentTypeBillSummary?.labelId?.toString()
            : "3070",
          documentUrl: response?.payload?.data?.billHeaderResponse
            ?.billSummaryLink
            ? `${API_URL}${response?.payload?.data?.billHeaderResponse?.billSummaryLink}`
            : "",
          documentLabelType: "BILL SUMMARY",
          deficiencyCheckStatus: null,
          referenceType: null,
          remarks: null,
        };
        setBillSummaryDocument(billSummary);
      }

      if (response?.payload?.data?.billHeaderResponse?.billAuditReportLink) {
        const documentTypeAuditReport = documentTypes?.find(
          (doc) => doc?.label?.toUpperCase() === "AUDIT REPORT",
        );

        const auditReport = {
          id: null,
          documentName: "AUDIT REPORT",
          documentType: documentTypeAuditReport?.labelId
            ? documentTypeAuditReport?.labelId?.toString()
            : "3082",
          documentUrl: response?.payload?.data?.billHeaderResponse
            ?.billAuditReportLink
            ? `${API_URL}${response?.payload?.data?.billHeaderResponse?.billAuditReportLink}`
            : "",
          documentLabelType: "AUDIT REPORT",
          deficiencyCheckStatus: null,
          referenceType: null,
          remarks: null,
        };
        setAuditReportDocument(auditReport);
      }
    } catch (error) {
      console.error("Error in fetching bill:", error);
    }
  };

  // useEffect(() => {
  //   fetchGovtBills();
  // }, [patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId]);

  const handleFileChange =
    (documentType: string, isMultiple: boolean) => (e: any) => {
      if (isMultiple) {
        const files = e?.fileList || [];

        if (e?.fileList[0]?.type !== "application/pdf") {
          errorMessage("File Must be PDF only");
          return;
        }

        const maxSize = 1 * 1024 * 1024;
        if (
          e?.fileList[0]?.size > maxSize &&
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
            "ECHS" &&
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
            "OP"
        ) {
          errorMessage("File Limit must be less than or equal to 1 MB");
          return;
        }

        setUploadFileList(files);
        if (files.length === 0) return;

        const selected = files?.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }));

        const previews = files?.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
          isMultiple: isMultiple,
        }));

        setSelectedFiles(selected);
        setPreviewFiles(previews);
        setPreviewModalVisible(true);
      } else {
        if (Array.isArray(e)) {
          return e;
        }

        if (e.file?.type !== "application/pdf") {
          errorMessage("File Must be PDF only");
          return;
        }

        const maxSize = 1 * 1024 * 1024;
        if (
          e?.file?.size > maxSize &&
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
            "ECHS" &&
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
            "OP"
        ) {
          errorMessage("File Limit must be less than or equal to 1 MB");
          return;
        }

        if (e?.file) {
          setSelectedFile({ file: e.file, documentType: documentType });
          setPreviewFile({
            url: URL.createObjectURL(e.file),
            name: e.file.name,
            showUpload: true,
            documentType: documentType,
          });
          setPreviewModalVisible(true);
        }
        return e?.target?.files;
      }
    };

  // const handleFileChange = (documentType: string) => (e: any) => {
  //   const maxSize = 15 * 1024 * 1024;

  //   if (Array.isArray(e)) {
  //     return e;
  //   }
  //   if (e?.file) {
  //     setSelectedFile({ file: e.file, documentType: documentType });
  //     setPreviewFile({
  //       url: URL.createObjectURL(e.file),
  //       name: e.file.name,
  //       showUpload: true,
  //       documentType: documentType,
  //     });
  //     setPreviewModalVisible(true);
  //   }
  //   return e?.target?.files;
  // };

  const fetchDocuments = async () => {
    if (schemeMetaDataId && caseId && visitId) {
      try {
        setLoading(true);
        const AllDocumentList = await getAllStarhealthDocumentsData(
          caseId,
          visitId,
          schemeMetaDataId,
        );
        const claimDocumentTypes = AllDocumentList?.["Claim"] || [];
        const apiClaimExtraDocs: ApiClaimExtraDocument[] =
          AllDocumentList?.claimExtraDocuments || [];
        setDocumentTypes(claimDocumentTypes);

        const billMappingId =
          patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId;
        let mappedBillRequiredDocuments: Document[] = [];
        if (billMappingId) {
          const billRequiredData = await getRequiredDocumentsByMaster(
            caseId,
            visitId,
            billMappingId,
          );
          if (billRequiredData?.documents?.length > 0) {
            mappedBillRequiredDocuments = mapBillApiToDocuments(
              billRequiredData?.documents,
              claimDocumentTypes,
            );
          }
        }

        const checklistUploadDocs = mergeBillRequiredUploadsFromClaimDocs(
          apiClaimExtraDocs,
          claimDocumentTypes,
          mappedBillRequiredDocuments,
        );
        mappedBillRequiredDocuments = mergeClaimExtraUploadsIntoBillDocuments(
          mappedBillRequiredDocuments,
          checklistUploadDocs,
        );
        setBillRequiredDocuments(mappedBillRequiredDocuments);
        setClaimExtraDocuments(
          mapApiClaimExtraDocumentsToState(
            checklistUploadDocs,
            mappedBillRequiredDocuments,
          ),
        );
      } catch (err) {
        errorMessage("Please, Try again after sometime");
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchDeficiencyData = async (
    selectedDocument: any,
    selectedDocumentId: any,
  ) => {
    try {
      if (selectedDocumentId) {
        // dispatch(clearDeficiencyCheck());
        // const firstRes = await dispatch(
        //   getDeficiencyQuestions(selectedDocumentId)
        // );
        setFetchingDeficiencyData(true);
        const secondRes = await dispatch(
          getDeficiencyQuestionData(selectedDocumentId),
        );
        setFetchingDeficiencyData(false);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
      setFetchingDeficiencyData(false);
    }
  };

  const [isFetchingRoomCharges, setIsFetchingRoomCharges] =
    useState<boolean>(false);
  const [isFetchingConsultationCharges, setIsFetchingConsultationCharges] =
    useState<boolean>(false);
  const [isFetchingImplantList, setIsFetchingImplantList] =
    useState<boolean>(false);

  const fetchRoomCharges = async () => {
    try {
      setIsFetchingRoomCharges(true);
      const roomChargesResponse = await getRoomCharges();
      const roomCharges = Object.entries(roomChargesResponse)?.map(
        ([key, value]) => ({
          value: key,
          label: key,
          cghsRate: value,
        }),
      );
      setRoomChargesOptions(roomCharges);
    } catch (err) {
      errorMessage("Room Charges options are not getting fetched");
    } finally {
      setIsFetchingRoomCharges(false);
    }
  };

  // const fetchConsultationCharges = async () => {
  //   try {
  //     setIsFetchingConsultationCharges(true);
  //     const consultationChargesResponse = await getConsultation();
  //     const consultationCharge = Object.entries(
  //       consultationChargesResponse,
  //     )?.map(([key, value]) => ({
  //       value: key,
  //       label: key,
  //       cghsRate: value,
  //     }));
  //     setConsultationChargesOptions(consultationCharge);
  //   } catch (err) {
  //     errorMessage("Room Charges options are not getting fetched");
  //   } finally {
  //     setIsFetchingConsultationCharges(false);
  //   }
  // };

  const fetchImplantList = async () => {
    try {
      if (
        patients?.fetchBasicDetails?.casesResponse?.healthCareId &&
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId
      ) {
        setIsFetchingImplantList(true);
        const implantResponse = await getImplants(
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        );
        setImplantListOptions(implantResponse);
      }
    } catch (err) {
      errorMessage("Room Charges options are not getting fetched");
    } finally {
      setIsFetchingImplantList(false);
    }
  };

  useEffect(() => {
    if (activeKey && activeKey?.length > 0 && isClaimTabActive()) {
      fetchRoomCharges();
      // fetchConsultationCharges();
      fetchImplantList();
    }
  }, [activeKey, patients?.fetchBasicDetails?.casesResponse?.healthCareId]);

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: any,
  ) => {
    await fetchDeficiencyData(document, document?.id);
    setPreviewDocumentUrl(documentUrl);
    // setDeficiencyModal(true);
    setSelectedDocument(document);
  };

  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as Document[],
      responseDocuments: [] as Document[],
    };

    documentTypes?.forEach((document) => {
      if (document?.requestDocument) {
        requestResponseDocuments.requestedDocuments.push(document);
      } else if (document?.responseDocument) {
        requestResponseDocuments.responseDocuments.push(document);
      }
    });

    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  const getBillRequiredDocumentLabels = (): string[] =>
    billRequiredDocuments
      ?.map((doc) => doc?.label?.toUpperCase())
      ?.filter(Boolean) as string[];

  const isBillRequiredDocumentLabel = (label?: string) =>
    getBillRequiredDocumentLabels()?.includes(label?.toUpperCase() || "");

  const excludeBillRequiredFromClaimAttachments = (docs: any[] = []) =>
    docs?.filter(
      (doc) => !isBillRequiredDocumentLabel(doc?.documentLabelType),
    ) || [];

  const getRegularClaimDocuments = () =>
    documentUrls?.filter(
      (doc) => !isBillRequiredDocumentLabel(doc?.documentLabelType),
    ) || [];

  const mapBillApiToDocuments = (
    billDocuments: BillRequiredDocumentApi[],
    allDocumentTypes: Document[],
  ): Document[] =>
    billDocuments?.map((billDoc) => {
      const schemeDoc = allDocumentTypes?.find(
        (doc) =>
          doc?.label?.toUpperCase() === billDoc?.documentName?.toUpperCase(),
      );
      const items: BillRequiredItem[] = billDoc?.items?.length
        ? billDoc.items
        : (billDoc?.itemName || []).map((itemName) => ({
            itemName,
            itemDate: "",
          }));
      return {
        id: billDoc?.id,
        label: billDoc?.documentName,
        labelId: schemeDoc?.labelId ?? billDoc?.id,
        documentName: billDoc?.documentName,
        requestDocument: true,
        required: true,
        isBillRequiredDocument: true,
        items,
        itemNames: items.map((item) => item.itemName),
      };
    }) || [];

  const mergeClaimExtraUploadsIntoBillDocuments = (
    billDocs: Document[],
    apiClaimExtraDocs: ApiClaimExtraDocument[] = [],
  ): Document[] =>
    billDocs.map((doc) => {
      const extraDoc = apiClaimExtraDocs.find(
        (entry) => entry?.label?.toUpperCase() === doc?.label?.toUpperCase(),
      );
      if (!extraDoc) return doc;
      return {
        ...doc,
        documentList: extraDoc?.documentList || [],
        data: extraDoc?.data ?? doc.data,
        multiple: extraDoc?.multiple ?? doc.multiple,
        itemNameEntries: extraDoc?.itemNames,
      };
    });

  /**
   * BE returns scheme Claim-stage docs under Claim, not claimExtraDocuments.
   * Merge those into the checklist upload source when they match bill-required labels.
   */
  const mergeBillRequiredUploadsFromClaimDocs = (
    apiClaimExtraDocs: ApiClaimExtraDocument[] = [],
    claimDocumentTypes: Document[] = [],
    billRequiredDocs: Document[] = [],
  ): ApiClaimExtraDocument[] => {
    const merged = [...(apiClaimExtraDocs || [])];
    const existingLabels = new Set(
      merged
        .map((entry) => entry?.label?.toUpperCase())
        .filter(Boolean) as string[],
    );

    billRequiredDocs?.forEach((billDoc) => {
      const label = billDoc?.label?.toUpperCase();
      if (!label || existingLabels.has(label)) return;

      const claimDoc = claimDocumentTypes?.find(
        (doc) => doc?.label?.toUpperCase() === label,
      );
      const hasUpload =
        Boolean(claimDoc?.documentList?.some((entry) => entry?.documentUrl)) ||
        Boolean(claimDoc?.data);
      if (!claimDoc || !hasUpload) return;

      merged.push({
        id: String(claimDoc.id ?? claimDoc.labelId ?? label),
        label: claimDoc.label,
        labelId: claimDoc.labelId ?? billDoc.labelId ?? billDoc.id,
        data: claimDoc.data,
        multiple: claimDoc.multiple,
        documentList: claimDoc.documentList || [],
      });
      existingLabels.add(label);
    });

    return merged;
  };

  const mapApiClaimExtraDocumentsToState = (
    apiClaimExtraDocs: ApiClaimExtraDocument[] = [],
    billRequiredDocs: Document[] = [],
  ): ClaimExtraDocument[] => {
    const flat: ClaimExtraDocument[] = [];

    apiClaimExtraDocs.forEach((docType) => {
      const billDoc = billRequiredDocs.find(
        (doc) => doc?.label?.toUpperCase() === docType?.label?.toUpperCase(),
      );

      const resolveItemNames = (
        docItemNames?: BillItemChecklistEntry[],
      ): BillItemChecklistEntry[] => {
        if (docItemNames?.length) return docItemNames;
        if (docType?.itemNames?.length) return docType.itemNames;
        if (billDoc?.items?.length) {
          return billDoc.items.map((item) => ({
            itemName: item.itemName,
            itemDate: item.itemDate,
            checked: true,
          }));
        }
        return (
          billDoc?.itemNames?.map((itemName) => ({
            itemName,
            checked: true,
          })) || []
        );
      };

      docType?.documentList?.forEach((doc) => {
        if (!doc?.documentUrl) return;
        flat.push({
          documentId: Number(
            docType?.labelId ?? billDoc?.labelId ?? billDoc?.id ?? 0,
          ),
          documentName: docType?.label || doc?.documentLabelType || "",
          documentType: doc?.documentType || String(docType?.labelId ?? ""),
          documentUrl: doc.documentUrl,
          documentLabelType: doc?.documentLabelType || docType?.label || "",
          id: doc?.id,
          itemNames: resolveItemNames(doc?.itemNames),
        });
      });
    });

    return flat;
  };

  const getUploadedBillExtraDocument = (doc?: Document | null) =>
    claimExtraDocuments?.find(
      (entry) =>
        entry?.documentName?.toUpperCase() === doc?.label?.toUpperCase() ||
        entry?.documentLabelType?.toUpperCase() === doc?.label?.toUpperCase(),
    );

  const resetBillRequiredPreview = () => {
    if (billRequiredPreviewFile.url) {
      URL.revokeObjectURL(billRequiredPreviewFile.url);
    }
    setBillRequiredPreviewVisible(false);
    setBillRequiredUploadContext(null);
    setBillRequiredPreviewFile({ url: "", name: "", file: null });
  };

  const handleBillRequiredFileSelect = (
    doc: Document,
    checkedItems: BillItemChecklistEntry[],
    file: File,
  ) => {
    setBillRequiredUploadContext({ doc, checkedItems });
    setBillRequiredPreviewFile({
      url: URL.createObjectURL(file),
      name: file.name,
      file,
    });
    setBillRequiredPreviewVisible(true);
  };

  const buildUploadFormData = (documentType: string | number, file: File) => {
    const CollectPostData = new FormData();
    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
      );
    }
    CollectPostData.append("documentType", String(documentType));
    CollectPostData.append("file", file);
    return CollectPostData;
  };

  const buildUpdatedClaimExtraDocuments = (
    uploadedDocs: any[],
    requestedDoc: Document,
    checkedItems: BillItemChecklistEntry[],
    existingExtraDocuments: ClaimExtraDocument[] = [],
  ): ClaimExtraDocument[] => {
    const selectedItems = checkedItems?.filter((entry) => entry.checked) || [];

    const extraEntries: ClaimExtraDocument[] = uploadedDocs?.map(
      (doc: any) => ({
        documentId: requestedDoc?.id,
        documentName: requestedDoc?.label || requestedDoc?.documentName || "",
        documentType: doc?.documentType,
        documentUrl: doc?.documentUrl,
        documentLabelType: doc?.documentLabelType,
        id: doc?.id,
        itemNames: selectedItems,
      }),
    );

    const uploadedUrls = new Set(
      extraEntries?.map((entry) => entry.documentUrl),
    );
    const filtered = existingExtraDocuments?.filter(
      (entry) => !uploadedUrls.has(entry.documentUrl),
    );
    return [...filtered, ...extraEntries];
  };

  const handleBillRequiredPreviewUpload = async () => {
    if (!billRequiredUploadContext?.doc || !billRequiredPreviewFile.file)
      return;

    await handleBillRequiredUpload(
      billRequiredPreviewFile.file,
      billRequiredUploadContext.checkedItems,
      billRequiredUploadContext.doc,
    );
  };

  const handleBillRequiredUpload = async (
    file: File,
    checkedItems: BillItemChecklistEntry[],
    doc: Document,
  ) => {
    const selectedItems = checkedItems?.filter((entry) => entry.checked);
    if (!selectedItems?.length) {
      errorMessage("Please select at least one bill item before uploading");
      return;
    }

    try {
      setBillRequiredUploading(true);
      const documentRes = await dispatch(
        UploadDocument({
          formData: buildUploadFormData(doc?.labelId ?? doc?.id, file),
          accessToken: auth.accessToken || "",
        }),
      ).unwrap();

      if (documentRes?.errorMessage) {
        errorMessage(documentRes.errorMessage);
        return;
      }

      const uploadedDocs = (
        Array.isArray(documentRes) ? documentRes : [documentRes]
      )?.filter((uploadedDoc: any) => uploadedDoc?.documentUrl);

      if (!uploadedDocs?.length) {
        errorMessage("Failed to upload document");
        return;
      }
      const withoutSameDocument =
        claimExtraDocuments?.filter(
          (entry) =>
            entry?.documentName?.toUpperCase() !== doc?.label?.toUpperCase(),
        ) || [];
      const updatedExtraDocuments = buildUpdatedClaimExtraDocuments(
        uploadedDocs,
        doc,
        checkedItems,
        withoutSameDocument,
      );

      setClaimExtraDocuments(updatedExtraDocuments);
      await handleAttachDocuments([], updatedExtraDocuments);
      message.success(`${doc?.label} uploaded successfully`);
      resetBillRequiredPreview();
    } catch (error) {
      console.error("Failed to upload bill required document:", error);
      errorMessage("Failed to upload document");
    } finally {
      setBillRequiredUploading(false);
    }
  };

  const handleBillRequiredDelete = async (
    doc: Document | null,
    documentUrl?: string,
  ) => {
    const uploadedDocument = documentUrl
      ? claimExtraDocuments?.find((entry) => entry.documentUrl === documentUrl)
      : getUploadedBillExtraDocument(doc);
    if (!uploadedDocument?.documentUrl) return;

    try {
      setBillRequiredUploading(true);
      await deleteUploadedDocument({
        documentUrl: uploadedDocument.documentUrl,
        status: false,
        id: uploadedDocument.id,
      });

      const updatedExtraDocuments =
        claimExtraDocuments?.filter(
          (entry) => entry.documentUrl !== uploadedDocument.documentUrl,
        ) || [];

      setClaimExtraDocuments(updatedExtraDocuments);
      await handleAttachDocuments([], updatedExtraDocuments);
      message.success("Document deleted successfully");
    } catch (error) {
      console.error("Failed to delete bill required document:", error);
      message.error("Please, Try again later");
    } finally {
      setBillRequiredUploading(false);
    }
  };

  const validateBillRequiredDocumentsUploaded = () => {
    if (!billRequiredDocuments?.length) {
      return true;
    }
    return billRequiredDocuments?.every((doc) =>
      claimExtraDocuments?.some(
        (extra) =>
          extra?.documentName?.toUpperCase() === doc?.label?.toUpperCase() ||
          extra?.documentLabelType?.toUpperCase() === doc?.label?.toUpperCase(),
      ),
    );
  };

  const supportingDocumentUrls = useMemo(
    () =>
      billRequiredDocuments?.flatMap((doc) => {
        const uploadedDocs =
          (doc?.documentList?.length ?? 0) > 0
            ? (doc.documentList ?? [])
            : claimExtraDocuments
                ?.filter(
                  (entry) =>
                    entry?.documentName?.toUpperCase() ===
                      doc?.label?.toUpperCase() ||
                    entry?.documentLabelType?.toUpperCase() ===
                      doc?.label?.toUpperCase(),
                )
                .map((entry) => ({
                  documentType: entry.documentType,
                  documentName: entry.documentName,
                  documentUrl: entry.documentUrl,
                  id: entry.id,
                  documentLabelType: entry.documentLabelType || doc.label || "",
                })) || [];

        return uploadedDocs
          ?.filter((entry: any) => entry?.documentUrl)
          .map((entry: any) => ({
            id: entry.id,
            documentName: entry.documentName,
            documentType: entry.documentType,
            documentUrl: entry.documentUrl,
            documentLabelType: entry.documentLabelType || doc.label,
            deficiencyCheckStatus: entry.deficiencyCheckStatus,
          }));
      }) || [],
    [billRequiredDocuments, claimExtraDocuments],
  );

  const handleOpenSupportingDocuments = (
    docType: string,
    documentUrl?: string,
  ) => {
    setSupportingPreviewOpen(true);
    setSupportingClickedActiveTab(docType);
    setSupportingClickedActiveDocumentUrl(documentUrl);
  };

  const handleCancelSupportingPreview = () => {
    setSupportingPreviewOpen(false);
    setSupportingClickedActiveTab(undefined);
    setSupportingClickedActiveDocumentUrl(undefined);
  };

  const handleSupportingDeleteList = async (
    documentType: string,
    documentUrl: string,
  ) => {
    const billDoc =
      billRequiredDocuments?.find(
        (doc) => doc?.label?.toUpperCase() === documentType?.toUpperCase(),
      ) || null;
    await handleBillRequiredDelete(billDoc, documentUrl);
  };

  const parseFormSectionData = (sectionData: any) => {
    if (!sectionData) return {};

    const parsedData: any = {};
    Object.entries(sectionData).forEach(([key, value]) => {
      if (typeof value === "string") {
        const [prefix, status, fieldValue] = (value as string).split(":");
        parsedData[key] = `${prefix}:${status}`;
        if (status === "Y" && fieldValue !== "null") {
          parsedData[`${key}Value`] = fieldValue;
        } else {
          parsedData[`${key}Value`] = "";
        }
      }
    });
    return parsedData;
  };

  const getClaimData = async () => {
    try {
      if (caseId && visitId) {
        setIsClaimLoading(true);
        const res = await getPreAuthClaimECHS(caseId, visitId);

        if (
          res?.claimSubmissionRes &&
          typeof res?.claimSubmissionRes === "object"
        ) {
          setClaimData(res.claimSubmissionRes);
          setCaseClaimId(res.claimSubmissionRes.id);
          setIsApproved(res.claimSubmissionRes.approved);

          const items = res.claimSubmissionRes?.procedureDataList?.map(
            (item: any) => ({
              expenseUnit: item?.expenseUnit ?? null,
              expenseCode: item?.expenseCode ?? null,
              admissibleRate: item?.admissibleRate ?? null,
              hospitalRate: item?.hospitalRate ?? null,
              hospitalAmount:
                item?.hospitalRate && item?.expenseUnit
                  ? parseFloat(item?.hospitalRate) *
                    parseFloat(item?.expenseUnit)
                  : null,
              hospitalRemark: item?.hospitalRemark ?? null,
              id: item?.id ?? null,
              procedureServiceName: item?.procedureServiceName ?? null,
              procedureID: item?.expenseName
                ? JSON.stringify({
                    rateType: item?.rateType ?? null,
                    expenseType: item?.expenseType ?? null,
                    expenseCode: item?.expenseCode ?? null,
                    procedureID: item?.procedureID ?? null,
                    expenseName: item?.expenseName ?? null,
                    // normalRate: item?.normalRate ?? null,
                    // admissibleRate: parseInt(item?.admissibleRate)?.toString() ?? null,
                  })
                : null,
            }),
          );

          const updatedValues = {
            ...res?.claimSubmissionRes,
            dateOfAdmission: res?.claimSubmissionRes?.dateOfAdmission
              ? dayjs(
                  res?.claimSubmissionRes?.dateOfAdmission,
                  "DD-MM-YYYY HH:mm:ss",
                )
              : null,
            billDate: res?.claimSubmissionRes?.billDate
              ? dayjs(res?.claimSubmissionRes?.billDate, "DD-MM-YYYY HH:mm:ss")
              : null,
            patientClinicalFindings:
              parseFormSectionData(
                res?.claimSubmissionRes?.patientClinicalFindings,
              ) || {},
            lineOfTreatmentData:
              parseFormSectionData(
                res?.claimSubmissionRes?.lineOfTreatmentData,
              ) || {},
            items: items?.length > 0 ? items : null,
            remarks: res.claimSubmissionRes.id
              ? res?.claimSubmissionRes?.remarks
              : "Claim Submitted",
          };

          form.setFieldsValue(updatedValues);

          setIsClaimLoading(false);
          // form.validateFields([["items", index, "hospitalRate"]]);
          // if (updatedValues.items && updatedValues.items?.length > 0) {
          //   const fields = updatedValues.items?.map((_: any, idx: any) => [
          //     "items",
          //     idx,
          //     "hospitalRate",
          //   ]);
          //   form.validateFields(fields, { validateOnly: false });
          // }
        }
      }
    } catch (err: any) {
      setIsClaimLoading(false);
      errorMessage(err?.message || "Please, Try again later");
    }
  };

  const fetchDataForSse = async () => {
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      // dispatch(
      //   getBasicDetails({
      //     id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      //   })
      // );
    }
    getClaimData();
    fetchStepperForStatus();
    await getTimelineDetail();
    fetchDocuments();
  };

  useEffect(() => {
    if (
      !claimData?.roomChargesList &&
      patients?.fetchBasicDetails?.patientResponse?.cardCategory?.toUpperCase() !==
        "PRIVATE"
    ) {
      const roomChargeOption = roomChargesOptions?.find(
        (room: any) =>
          room?.value?.toLowerCase() ===
          patients?.fetchBasicDetails?.patientResponse?.cardCategory?.toLowerCase(),
      );

      const updatedRoomChargesList = [
        {
          daysUnits: 0,
          cghsRate: roomChargeOption?.cghsRate,
          hospitalRate: 0,
          cghsCode: roomChargeOption?.value,
          type: "roomCharges",
        },
      ];

      form.setFieldsValue({ roomChargesList: updatedRoomChargesList });
    }
  }, [
    patients?.fetchBasicDetails?.patientResponse?.cardCategory,
    roomChargesOptions,
    claimData,
  ]);

  useEffect(() => {
    if (sseData) {
      fetchDataForSse();
    }
  }, [sseData, sseUpdateCount]);

  useEffect(() => {
    const items = form.getFieldValue("items");
    if (items && items?.length > 0) {
      const fields = items?.map((_: any, idx: any) => [
        "items",
        idx,
        "hospitalRate",
      ]);
      form.validateFields(fields, { validateOnly: false });
    }
  }, [form.getFieldValue("items")]);

  useEffect(() => {
    const items = form.getFieldValue("items");
    if (items && items?.length > 0) {
      const fields = items?.map((_: any, idx: any) => [
        "items",
        idx,
        "hospitalRate",
      ]);
      form.validateFields(fields, { validateOnly: false });
    }
  }, [form.getFieldValue("items")]);

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId &&
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "ECHS" &&
      patients?.fetchBasicDetails?.casesResponse?.healthCareId &&
      isClaimTabActive()
    ) {
      // dispatch(
      //   fetchProcedureNew({
      //     caseId: patients?.fetchBasicDetails?.casesResponse?.id,
      //     hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
      //     isCghs: true,
      //     procedureType:
      //       patients?.fetchBasicDetails?.visitInfoResponse?.visitType === "OP"
      //         ? "OP"
      //         : "IP",
      //     schemeId:
      //       96,
      //   })
      // );

      dispatch(
        fetchEchsProcedure({
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patientType:
            patients?.fetchBasicDetails?.visitInfoResponse?.visitType === "OP"
              ? "OP"
              : "IP",
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        }),
      );
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.healthCareId,
    patients?.fetchBasicDetails?.casesResponse?.caseTypeMetaDataName,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    activeKey,
  ]);

  const fetchStepperForStatus = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      await dispatch(fetchStepperData({ caseId: caseId, visitId: visitId }));
    }
  };

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  };

  useEffect(() => {
    if (activeKey && activeKey?.length > 0 && isClaimTabActive()) {
      // fetchStepperForStatus();
      // getTimelineDetail();
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      await getClaimData();
      await fetchDocuments();
    };

    if (activeKey && activeKey?.length > 0 && isClaimTabActive()) fetchData();
  }, [schemeMetaDataId, activeKey]);

  const handleOpen = (docType: any, documentUrl: any) => {
    const isBillRequired =
      isBillRequiredDocumentLabel(docType) ||
      claimExtraDocuments?.some(
        (entry) =>
          entry.documentUrl === documentUrl ||
          entry.documentName?.toUpperCase() === docType?.toUpperCase() ||
          entry.documentLabelType?.toUpperCase() === docType?.toUpperCase(),
      );

    if (isBillRequired) {
      setSupportingPreviewOpen(true);
      setSupportingClickedActiveTab(docType);
      setSupportingClickedActiveDocumentUrl(documentUrl);
      return;
    }

    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleCancelPreview = () => {
    setPreviewModalVisible(false);
    setSelectedFile(null);
    setPreviewDocument(false);
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrls: any,
  ) => {
    for (const doc of mandatoryDocuments) {
      if (
        doc.required ||
        (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
          "ECHS" &&
          patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
            "OP" &&
          (patients?.fetchBasicDetails?.patientResponse?.age < 70 ||
            patients?.fetchBasicDetails?.referralInfoResponse
              ?.echsReferralResponse?.referralNo) &&
          doc?.label?.toLowerCase()?.includes("referral letter"))
      ) {
        const isUploaded = documentUrls?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label,
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  const updateOrAddDocument = (
    uploadedDocumentUrl: any,
    documentRes: IDocumentFields,
  ) => {
    const updatedDocuments = [...uploadedDocumentUrl];
    const documentIndex = updatedDocuments.findIndex(
      (doc) => doc.documentType === documentRes.documentType,
    );

    if (
      documentIndex !== -1 &&
      documentRes?.documentType?.toUpperCase() !== "DOCUMENT" &&
      documentRes?.documentLabelType?.toUpperCase() !== "AUDITED BILL" &&
      documentRes?.documentLabelType?.toUpperCase() !== "HOSPITAL BILL" &&
      documentRes?.documentLabelType?.toUpperCase() !== "COVID TEST REPORT" &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Unlisted Procedure Docs"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Unlisted Procedure Permission"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Consultation Notes"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "DISCHARGE SUMMARY"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "DRUG CERTIFICATE"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "DEATH CERTIFICATE"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "ECHS CARD COPY"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "EMERGENCY LETTER"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "OTHERS"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "LAB REPORTS"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "SELF ATTESTED PROFORMA"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !== "MLC"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Contingent Bill"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Copy of Cancelled Cheque"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Delay Condonation Letter (Intimation)"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Delay Condonation Letter (Submission)"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Disability Certificate (Intimation)"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Disability Certificate (Submission)"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Doctor Certificate"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !== "EIR"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Extended Stay Certificate"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Extension of Stay - Approval"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Extension of Stay - Permission"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Feedback Form"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Geo-Tagged Photo of Patient"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Medical Reports"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Medicine Issue Slip"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "NA Certificate"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Outer Pack of Implants"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Personal Application"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Prescription Slip"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Referral Letter (By Polyclinic)"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Emergency Letter (By Hospital)"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Sanction Letter"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Service Specialist Recommendation"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Stay Extension Documents"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Supplimentary Justification Document"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Supplimentary Sanction Document"?.toUpperCase() &&
      documentRes?.documentLabelType?.toUpperCase() !==
        "Unlisted Procedure Approval"?.toUpperCase()
    ) {
      updatedDocuments[documentIndex] = {
        ...updatedDocuments[documentIndex],
        documentName: documentRes?.documentName,
        documentUrl: documentRes?.documentUrl,
        deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
        id: documentRes?.id,
      };
    } else {
      updatedDocuments.push({
        documentType: documentRes.documentType,
        documentName: documentRes.documentName,
        documentUrl: documentRes.documentUrl,
        documentLabelType: documentRes?.documentLabelType,
        deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
        id: documentRes?.id,
      });
    }
    return updatedDocuments;
  };

  const handleSubmit = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);

      // const updatedPatientClinicalFindings = Object.keys(
      //   values?.patientClinicalFindings || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.patientClinicalFindings[key];
      //     const inputValue = values?.patientClinicalFindings[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedPatientClinicalFindings = {};

      if (
        values?.patientClinicalFindings &&
        Object.keys(values.patientClinicalFindings).length > 0
      ) {
        updatedPatientClinicalFindings = Object.keys(
          values.patientClinicalFindings,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.patientClinicalFindings[key];
            const inputValue = values.patientClinicalFindings[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedPatientClinicalFindings = clinicalConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "bp") {
              acc[condition.key] = `${condition.valuePrefix}:Y:120/80`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      // Process treatment data
      // const updatedLineOfTreatmentData = Object.keys(
      //   values?.lineOfTreatmentData || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.lineOfTreatmentData[key];
      //     const inputValue = values?.lineOfTreatmentData[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedLineOfTreatmentData = {};

      if (
        values?.lineOfTreatmentData &&
        Object.keys(values.lineOfTreatmentData).length > 0
      ) {
        updatedLineOfTreatmentData = Object.keys(
          values.lineOfTreatmentData,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.lineOfTreatmentData[key];
            const inputValue = values.lineOfTreatmentData[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedLineOfTreatmentData = treatmentConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "medicalManagement") {
              acc[condition.key] =
                `${condition.valuePrefix}:Y:supportive treatment`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      const updatedValues = {
        ...values,
        status: true,
        // claimNo: values?.claimNo,
        documents: getRegularClaimDocuments(),
        claimExtraDocuments: claimExtraDocuments,
        totalAmount: values?.claimedAmount,
        dateOfAdmission: values?.dateOfAdmission
          ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss",
            )
          : null,
        billDate: values?.billDate
          ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss",
            )
          : null,
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        deletedProcedureList: deleteProcedureList,
        patientClinicalFindings: updatedPatientClinicalFindings,
        lineOfTreatmentData: updatedLineOfTreatmentData,
        procedureDataList: values?.items?.map((data: any) => {
          return {
            ...JSON.parse(data.procedureID || "{}"),
            ...data,
            procedureID: JSON.parse(data.procedureID).procedureID,
          };
        }),
      };

      setIsSaving(true);

      if (
        patients?.fetchBasicDetails?.casesResponse?.id &&
        patients?.fetchBasicDetails?.visitInfoResponse?.id &&
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds
      ) {
        setIsSaving(true);
        const resp = await dispatch(
          submitClaimSubmissionToEdgeProEchs({
            data: updatedValues,
            caseId: patients?.fetchBasicDetails?.casesResponse?.id,
            visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
            portalId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            caseClaimId: caseClaimId,
            // caseClaimId: null,
          }),
        );
        setIsSaving(false);

        if (submitClaimSubmissionToEdgeProEchs.fulfilled.match(resp)) {
          successMessage("Claim Saved Successfully");
          setConfirmationModal(false);
          await getClaimData();
          await fetchStepperForStatus();
          await getTimelineDetail();
          await fetchDocuments();
          if (visitId) {
            dispatch(
              getBasicDetails({
                id: visitId,
              }),
            );
          }
        } else if (submitClaimSubmissionToEdgeProEchs.rejected.match(resp)) {
          errorMessage("Please, Try again later");
          setConfirmationModal(false);
          setIsSaving(false);
        }
      }
    } catch (error: any) {
      if (error?.errorFields) {
        errorMessage("Please, fill all details before submitting");
        setIsSaving(false);
        setConfirmationModal(false);
      } else {
        errorMessage(error?.message || "Something went wrong");
        setIsSaving(false);
        setConfirmationModal(false);
      }
    } finally {
      setIsSaving(false);
      setConfirmationModal(false);
      setDeleteProcedureList([]);
    }
  };

  const handleSubmitOnPortal = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);

      // const updatedPatientClinicalFindings = Object.keys(
      //   values?.patientClinicalFindings || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.patientClinicalFindings[key];
      //     const inputValue = values?.patientClinicalFindings[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedPatientClinicalFindings = {};

      if (
        values?.patientClinicalFindings &&
        Object.keys(values.patientClinicalFindings).length > 0
      ) {
        updatedPatientClinicalFindings = Object.keys(
          values.patientClinicalFindings,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.patientClinicalFindings[key];
            const inputValue = values.patientClinicalFindings[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedPatientClinicalFindings = clinicalConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "bp") {
              acc[condition.key] = `${condition.valuePrefix}:Y:120/80`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      // Process treatment data
      // const updatedLineOfTreatmentData = Object.keys(
      //   values?.lineOfTreatmentData || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.lineOfTreatmentData[key];
      //     const inputValue = values?.lineOfTreatmentData[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedLineOfTreatmentData = {};

      if (
        values?.lineOfTreatmentData &&
        Object.keys(values.lineOfTreatmentData).length > 0
      ) {
        updatedLineOfTreatmentData = Object.keys(
          values.lineOfTreatmentData,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.lineOfTreatmentData[key];
            const inputValue = values.lineOfTreatmentData[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedLineOfTreatmentData = treatmentConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "medicalManagement") {
              acc[condition.key] =
                `${condition.valuePrefix}:Y:supportive treatment`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
        // && validateBillRequiredDocumentsUploaded()
      ) {
        const updatedValues = {
          ...values,
          status: true,
          // claimNo: values?.claimNo,
          documents: getRegularClaimDocuments(),
          claimExtraDocuments: claimExtraDocuments,
          totalAmount: values?.claimedAmount,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          billDate: values?.billDate
            ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          integratedWithPortal:
            patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          deletedProcedureList: deleteProcedureList,
          patientClinicalFindings: updatedPatientClinicalFindings,
          lineOfTreatmentData: updatedLineOfTreatmentData,
          procedureDataList: values?.items?.map((data: any) => {
            return {
              ...JSON.parse(data.procedureID || "{}"),
              ...data,
              procedureID: JSON.parse(data.procedureID).procedureID,
            };
          }),
        };

        setIsSubmitting(true);

        if (
          patients?.fetchBasicDetails?.casesResponse?.id &&
          patients?.fetchBasicDetails?.visitInfoResponse?.id &&
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds
        ) {
          setIsSubmitting(true);
          const resp = await dispatch(
            submitClaimSubmissionEchs({
              data: updatedValues,
              caseId: patients?.fetchBasicDetails?.casesResponse?.id,
              visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              portalId:
                patients?.fetchBasicDetails?.casesResponse
                  ?.portalMetaDataIds?.[0],
              // caseClaimId: null
            }),
          );
          setIsSubmitting(false);

          if (submitClaimSubmissionEchs.fulfilled.match(resp)) {
            successMessage("Claim Submitted Successfully");
            setConfirmationModal(false);
            await getClaimData();
            await fetchStepperForStatus();
            await getTimelineDetail();
            await fetchDocuments();
            if (visitId) {
              dispatch(
                getBasicDetails({
                  id: visitId,
                }),
              );
            }
          } else if (submitClaimSubmissionEchs.rejected.match(resp)) {
            errorMessage("Please, Try again later");
            setConfirmationModal(false);
            setIsSubmitting(false);
          }
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsSubmitting(false);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        errorMessage("Please, fill all details before submitting");
        setIsSubmitting(false);
        setConfirmationModal(false);
      } else {
        errorMessage(error?.message || "Something went wrong");
        setConfirmationModal(false);
        setIsSubmitting(false);
      }
    } finally {
      setIsSubmitting(false);
      setConfirmationModal(false);
      setDeleteProcedureList([]);
      setIsFetchingDigitallySignedDocs(false);
    }
  };

  const handleApprove = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);

      // const updatedPatientClinicalFindings = Object.keys(
      //   values?.patientClinicalFindings || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.patientClinicalFindings[key];
      //     const inputValue = values?.patientClinicalFindings[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedPatientClinicalFindings = {};

      if (
        values?.patientClinicalFindings &&
        Object.keys(values.patientClinicalFindings).length > 0
      ) {
        updatedPatientClinicalFindings = Object.keys(
          values.patientClinicalFindings,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.patientClinicalFindings[key];
            const inputValue = values.patientClinicalFindings[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedPatientClinicalFindings = clinicalConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "bp") {
              acc[condition.key] = `${condition.valuePrefix}:Y:120/80`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      // Process treatment data
      // const updatedLineOfTreatmentData = Object.keys(
      //   values?.lineOfTreatmentData || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.lineOfTreatmentData[key];
      //     const inputValue = values?.lineOfTreatmentData[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedLineOfTreatmentData = {};

      if (
        values?.lineOfTreatmentData &&
        Object.keys(values.lineOfTreatmentData).length > 0
      ) {
        updatedLineOfTreatmentData = Object.keys(
          values.lineOfTreatmentData,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.lineOfTreatmentData[key];
            const inputValue = values.lineOfTreatmentData[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedLineOfTreatmentData = treatmentConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "medicalManagement") {
              acc[condition.key] =
                `${condition.valuePrefix}:Y:supportive treatment`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
        // && validateBillRequiredDocumentsUploaded()
      ) {
        setIsApprovedInitiated(true);
        const updatedValues = {
          ...values,
          status: true,
          // claimNo: values?.claimNo,
          documents: getRegularClaimDocuments(),
          claimExtraDocuments: claimExtraDocuments,
          totalAmount: values?.claimedAmount,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          billDate: values?.billDate
            ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          patientClinicalFindings: updatedPatientClinicalFindings,
          lineOfTreatmentData: updatedLineOfTreatmentData,
          deletedProcedureList: deleteProcedureList,
          procedureDataList: values?.items?.map((data: any) => {
            return {
              ...JSON.parse(data.procedureID || "{}"),
              ...data,
              procedureID: JSON.parse(data.procedureID).procedureID,
            };
          }),
        };

        const formData = {
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          stage: "Claim",
          approved: true,
          claimRequest: updatedValues,
          caseClaimSubmissionId: caseClaimId,
        };

        const res = await submitApprovalEchs(formData);
        setIsApproved(true);
        setIsApprovedInitiated(false);
        message.success("Claim Approved Successfully");
        setConfirmationModal(false);
        setDeleteProcedureList([]);
        await getTimelineDetail();
        await fetchStepperForStatus();
        await getClaimData();
        await fetchDocuments();
        if (visitId) {
          dispatch(
            getBasicDetails({
              id: visitId,
            }),
          );
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, fill all details before submitting");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      } else {
        errorMessage("Please, Try again later");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
      setDeleteProcedureList([]);
      setConfirmationModal(false);
    }
  };

  const handleAttachDocuments = async (
    updatedDocumentUrls: any,
    extraDocuments: ClaimExtraDocument[] = claimExtraDocuments,
  ) => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
      claimExtraDocuments: ClaimExtraDocument[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
      claimExtraDocuments: extraDocuments,
    };

    payloadSchema["caseClaimAttachments"] =
      excludeBillRequiredFromClaimAttachments(updatedDocumentUrls);

    if (caseId && visitId && schemeMetaDataId) {
      try {
        await updateAllDocumentsData(caseId, visitId, payloadSchema);
        message.success(`documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleSaveData = async () => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
      claimExtraDocuments: ClaimExtraDocument[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
      claimExtraDocuments: claimExtraDocuments,
    };

    const updatedDocumentUrls: any = documentUrls?.filter((document: any) =>
      documentRequestResponseDocuments["responseDocuments"]?.some(
        (doc: any) => doc?.label === document?.documentLabelType,
      ),
    );

    payloadSchema["caseClaimAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema,
        );
        message.success(`Response documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleIntimate = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);

      // const updatedPatientClinicalFindings = Object.keys(
      //   values?.patientClinicalFindings || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.patientClinicalFindings[key];
      //     const inputValue = values?.patientClinicalFindings[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedPatientClinicalFindings = {};

      if (
        values?.patientClinicalFindings &&
        Object.keys(values.patientClinicalFindings).length > 0
      ) {
        updatedPatientClinicalFindings = Object.keys(
          values.patientClinicalFindings,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.patientClinicalFindings[key];
            const inputValue = values.patientClinicalFindings[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedPatientClinicalFindings = clinicalConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "bp") {
              acc[condition.key] = `${condition.valuePrefix}:Y:120/80`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      // Process treatment data
      // const updatedLineOfTreatmentData = Object.keys(
      //   values?.lineOfTreatmentData || {},
      // ).reduce((acc: any, key: string) => {
      //   if (!key.includes("Value")) {
      //     const baseValue = values?.lineOfTreatmentData[key];
      //     const inputValue = values?.lineOfTreatmentData[`${key}Value`];
      //     acc[key] = baseValue + ":" + (inputValue || null);
      //   }
      //   return acc;
      // }, {});

      let updatedLineOfTreatmentData = {};

      if (
        values?.lineOfTreatmentData &&
        Object.keys(values.lineOfTreatmentData).length > 0
      ) {
        updatedLineOfTreatmentData = Object.keys(
          values.lineOfTreatmentData,
        ).reduce((acc: any, key: string) => {
          if (!key.includes("Value")) {
            const baseValue = values.lineOfTreatmentData[key];
            const inputValue = values.lineOfTreatmentData[`${key}Value`];

            acc[key] = baseValue + ":" + (inputValue || null);
          }
          return acc;
        }, {});
      } else {
        updatedLineOfTreatmentData = treatmentConditions.reduce(
          (acc: any, condition: any) => {
            if (condition.key === "medicalManagement") {
              acc[condition.key] =
                `${condition.valuePrefix}:Y:supportive treatment`;
            } else {
              acc[condition.key] = `${condition.valuePrefix}:M:null`;
            }

            return acc;
          },
          {},
        );
      }

      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
        // && validateBillRequiredDocumentsUploaded()
      ) {
        const updatedValues = {
          ...values,
          status: true,
          // claimNo: values?.claimNo,
          documents: getRegularClaimDocuments(),
          claimExtraDocuments: claimExtraDocuments,
          totalAmount: values?.claimedAmount,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          billDate: values?.billDate
            ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          patientClinicalFindings: updatedPatientClinicalFindings,
          lineOfTreatmentData: updatedLineOfTreatmentData,
          action: "intimate",
          deletedProcedureList: deleteProcedureList,
          procedureDataList: values?.items?.map((data: any) => {
            return {
              ...JSON.parse(data.procedureID || "{}"),
              ...data,
              procedureID: JSON.parse(data.procedureID).procedureID,
            };
          }),
        };

        if (
          patients?.fetchBasicDetails?.casesResponse?.id &&
          patients?.fetchBasicDetails?.visitInfoResponse?.id &&
          patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds
        ) {
          setIsIntimating(true);
          const resp = await dispatch(
            intimateClaimSubmissionEchs({
              data: updatedValues,
              caseId: patients?.fetchBasicDetails?.casesResponse?.id,
              visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              portalId:
                patients?.fetchBasicDetails?.casesResponse
                  ?.portalMetaDataIds?.[0],
              caseClaimId: caseClaimId,
              // caseClaimId: null,
            }),
          );
          setIsIntimating(false);

          if (intimateClaimSubmissionEchs.fulfilled.match(resp)) {
            successMessage("Claim Sent To QC Successfully");
            setConfirmationModal(false);
            await getClaimData();
            await fetchStepperForStatus();
            await getTimelineDetail();
            await fetchDocuments();
            if (visitId) {
              dispatch(
                getBasicDetails({
                  id: visitId,
                }),
              );
            }
          } else if (intimateClaimSubmissionEchs.rejected.match(resp)) {
            errorMessage("Please, Try again later");
            setIsIntimating(false);
            setConfirmationModal(false);
            setDeleteProcedureList([]);
          }
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsIntimating(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, fill all details before submitting");
      } else {
        errorMessage("Please, Try again later");
      }
      setIsIntimating(false);
      setConfirmationModal(false);
      setDeleteProcedureList([]);
    }
  };

  const handleDeleteProcedure = async (procedure: any) => {
    try {
      const updatedProcedure = procedure.procedureID
        ? {
            ...JSON.parse(procedure.procedureID || "{}"),
            ...procedure,
            procedureID: JSON.parse(procedure.procedureID).procedureID,
          }
        : procedure;
      setDeleteProcedureList((prevList) => [...prevList, updatedProcedure]);
      successMessage("Procedure deleted successfully");
      return true;
    } catch (err: any) {
      errorMessage(err?.response?.data || "Please, Try again after sometime");
      return false;
    }
  };

  // const handleDeleteProcedure = async (procedureBackendId: any) => {
  //     try {
  //       if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
  //         await deleteProcedureEchs(
  //           patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
  //           {
  //             id: procedureBackendId,
  //             status: false,
  //           }
  //         );

  //         successMessage("Procedure deleted successfully");
  //         return true;
  //       }
  //     } catch (err: any) {
  //       errorMessage(err?.response?.data || "Please, Try again after sometime");
  //       return false;
  //     }
  //   };

  const handleUpload = async () => {
    if (selectedFiles?.length > 0) {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
        );
      }
      CollectPostData.append("documentType", previewFiles?.[0]?.documentType);

      selectedFiles?.map((fileObj, index) =>
        CollectPostData.append(`file${index}`, fileObj.file),
      );

      // if (selectedFiles?.length > 0) {
      //   selectedFiles.forEach((fileObj, index) => {
      //     // Extract the actual File object from your structure
      //     const actualFile = fileObj.file; // This is the originFileObj (actual File)

      //     if (actualFile instanceof File) {
      //       CollectPostData.append("files", actualFile);
      //     }
      //   });
      // }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          }),
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
        );
        await handleAttachDocuments(documentRes, claimExtraDocuments);
        setUploadFileList([]);
        setSelectedFiles([]);
        setPreviewFiles([]);
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    } else {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
        );
      }
      CollectPostData.append("documentType", previewFile.documentType);

      if (selectedFile?.file) {
        CollectPostData.append("file", selectedFile?.file);
      }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          }),
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
        );
        await handleAttachDocuments(documentRes, claimExtraDocuments);
        setSelectedFile(null);
        setPreviewFile({
          url: "",
          name: "",
          showUpload: false,
          documentType: "",
        });
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    }
  };

  // const handleUpload = async () => {
  //   const CollectPostData = new FormData();
  //   if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
  //     CollectPostData.append(
  //       "hospitalId",
  //       patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.patientResponse?.id) {
  //     CollectPostData.append(
  //       "patientId",
  //       patients?.fetchBasicDetails?.patientResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.casesResponse?.id) {
  //     CollectPostData.append(
  //       "caseId",
  //       patients?.fetchBasicDetails?.casesResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
  //     CollectPostData.append(
  //       "visitId",
  //       patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
  //     );
  //   }
  //   CollectPostData.append("documentType", previewFile.documentType);

  //   if (selectedFile?.file) {
  //     CollectPostData.append("file", selectedFile?.file);
  //   }

  //   try {
  //     const documentRes = await dispatch(
  //       UploadDocument({
  //         formData: CollectPostData,
  //         accessToken: auth.accessToken || "",
  //       })
  //     ).unwrap();

  //     const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);

  //     setDocumentUrls(updatedDocuments);
  //     setPreviewModalVisible(false);
  //     message.success(
  //       `${documentRes?.documentLabelType} uploaded successfully`
  //     );
  //   } catch (error) {
  //     console.error("Failed to upload document:", error);
  //   }
  // };

  const handleBasicDetailsUpdate = async () => {
    if (!caseId || !visitId) {
      errorMessage("Required information not found");
      return;
    }

    try {
      await dispatch(getBasicDetails({ id: caseId }));
      await dispatch(getTimelineDetails({ caseId, visitId }));
      await dispatch(fetchStepperData({ caseId, visitId }));
    } catch (error: any) {
      errorMessage(error?.message || "Failed to update details");
    }
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      await downloadDocument(fileName, url);
      successMessage("document downloaded successfully");
    } catch (err) {
      errorMessage("Please, Try again later");
    }
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number,
  ) => {
    const combinedUrls: any = [...documentUrls];
    const deleteDocument = combinedUrls?.find(
      (doc: any, index: any) => doc?.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    const newUpdatedDocumentList = documentUrls?.filter(
      (doc: any, index: any) => doc?.documentUrl !== documentUrl,
    );
    setDocumentUrls(newUpdatedDocumentList);

    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
      } catch (err) {
        console.error(err);
        message.error("Please, Try again later");
      }
      // fetchDocuments();
    }
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setSelectedDocument(null);
  };

  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [confirmationState, setConfirmationState] = useState<string>("");

  const handleSaveConfirmationModal = async (confirmationState: string) => {
    setIsFetchingDigitallySignedDocs(true);
    if (confirmationState === "submit") {
      const payload =
        documentUrls?.map((document: any) => ({
          documentUrl: document?.documentUrl,
          documentLabelType: document?.documentLabelType,
          documentName: document?.documentName,
          documentId: document?.id,
        })) || [];

      const res = await getDigitallySignedDocument(payload);
      setIsFetchingDigitallySignedDocs(false);
      const results = res?.results || [];

      setDigitallySignedDocuments(results);

      const unsigned = results.filter((doc: any) => !doc?.digitallySigned);

      if (unsigned.length > 0) {
        setUnsignedDocumentsCount(unsigned.length);
        setShowUnsignedDocumentsModal(true);
        return;
      } else {
        setIsAllDigitallySignedModal(true);
        return;
      }
    }

    if (confirmationState === "intimate") {
      if (auth?.role?.role !== "FO_STAFF") {
        try {
          await form.validateFields();
        } catch (err: any) {
          setIsFetchingDigitallySignedDocs(false);
          if (err?.errorFields) {
            errorMessage("Please, fill all details before submitting");
          } else {
            errorMessage("Please, Try again later");
          }
          return;
        }
      }
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsFetchingDigitallySignedDocs(false);
        errorMessage("Please, Upload all required documents");
        return;
      }

      const payload =
        documentUrls?.map((document: any) => ({
          documentUrl: document?.documentUrl,
          documentLabelType: document?.documentLabelType,
          documentName: document?.documentName,
          documentId: document?.id,
        })) || [];

      const res = await getDigitallySignedDocument(payload);
      setIsFetchingDigitallySignedDocs(false);
      const results = res?.results || [];

      setDigitallySignedDocuments(results);

      const unsigned = results.filter((doc: any) => !doc?.digitallySigned);

      if (unsigned.length > 0) {
        setUnsignedDocumentsCount(unsigned.length);
        setConfirmationState("intimate");
        setShowUnsignedDocumentsModal(true);
        return;
      } else {
        setConfirmationState("intimate");
        setIsAllDigitallySignedModal(true);
        return;
      }
    }

    setConfirmationModal(true);
    setConfirmationState(confirmationState);
    setIsFetchingDigitallySignedDocs(false);

    if (confirmationState === "save") {
      setTitle("Do You want to save Claim ?");
    } else if (confirmationState === "update") {
      setTitle("Do You want to update Claim ?");
    } else if (confirmationState === "submit") {
      setTitle("Do You want to submit Claim ?");
    } else if (confirmationState === "approve") {
      setTitle("Do You want to approve Claim ?");
    } else if (confirmationState === "intimate") {
      setTitle("Do you want to send Claim for QC ?");
    }
  };

  const handleSave = async () => {
    if (confirmationState === "save") {
      await handleSubmit();
    } else if (confirmationState === "update") {
      await handleSubmit();
    } else if (confirmationState === "submit") {
      await handleSubmitOnPortal();
    } else if (confirmationState === "approve") {
      await handleApprove();
    } else if (confirmationState === "intimate") {
      await handleIntimate();
    }

    setConfirmationModal(false);
  };

  useEffect(() => {
    const updatedData = {
      fullName: patients?.fetchBasicDetails?.patientResponse?.fullName,
      age: patients?.fetchBasicDetails?.patientResponse?.age,
      primaryMobile:
        patients?.fetchBasicDetails?.patientResponse?.primaryMobile,
      referralType:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.referralType,
      patientType: patients?.fetchBasicDetails?.visitInfoResponse?.visitType,
      category:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.cardcategory,
      gender: patients?.fetchBasicDetails?.patientResponse?.sex,
      email: patients?.fetchBasicDetails?.patientResponse?.email,
      referralNo:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.referralNo,
      referralExpiryFrom:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.referralExpiryFrom,
      referralExpiryTo:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.referralExpiryTo,
      admission:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.admission,
      investigation:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.investigation,
      consultationFor:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.consultationFor,
      noOfVisits:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.noOfVisits,
      polyClinic:
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.polyClinic,
    };

    form.setFieldsValue(updatedData);
  }, [patients?.fetchBasicDetails]);

  const [isAmountModal, setIsAmountModal] = useState<boolean>(false);

  const [billItemsList, setBillItemsList] = useState<any[]>([]);
  const [govtBillMappingId, setGovtBillMappingId] = useState<number | null>(
    null,
  );

  const fetchHospitalBillList = async () => {
    try {
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        const filters = {
          pageNumber: 1,
          pageSize: 10,
          searchTer: "",
        };
        const response = await getGovtBillList(
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          filters,
        );

        setGovtBillMappingId(
          response?.data?.billMappingResponseList?.length
            ? response.data.billMappingResponseList?.[0].id
            : null,
        );
      }
    } catch (err) {}
  };

  const items = Form.useWatch("items", form);

  useEffect(() => {
    if (activeKey && activeKey?.length > 0 && isClaimTabActive()) {
      fetchHospitalBillList();
    }
  }, [activeKey]);

  // useEffect(() => {
  //   if((!claimData?.procedureDataList || !claimData?.procedureDataList?.length) && activeKey && activeKey?.length > 0 && isClaimTabActive()) {
  //     fetchGovtBill();
  //   }
  // }, [govtBillMappingId, activeKey, claimData])

  useEffect(() => {
    const procedures = claimData?.procedureDataList || [];

    const isEmpty = !procedures || procedures.length === 0;

    const hasSingleWithoutExpenseName =
      procedures?.length === 1 && !procedures[0]?.expenseName?.trim();

    if (
      (isEmpty || hasSingleWithoutExpenseName) &&
      activeKey &&
      activeKey?.length > 0 &&
      isClaimTabActive()
    ) {
      fetchGovtBill();
    }
  }, [govtBillMappingId, activeKey, claimData]);

  const fetchGovtBill = async () => {
    try {
      if (
        !patients?.fetchBasicDetails?.casesResponse?.healthCareId ||
        !govtBillMappingId
      )
        return;
      const response = await dispatch(
        GovtBillFetchRequest({
          healthCareId:
            patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          govtBillMappingId: govtBillMappingId,
        }),
      );
      setBillItemsList(response?.payload?.data?.billItemsResponseList || []);
      if (!form.getFieldValue("billDate")) {
        form.setFieldsValue({
          billDate: response?.payload?.data?.billHeaderResponse?.billDateTime
            ? dayjs(
                response?.payload?.data?.billHeaderResponse?.billDateTime,
                "DD/MM/YYYY",
              )
            : null,
        });
      }

      if (!form.getFieldValue("billNumber")) {
        form.setFieldsValue({
          billNumber: response?.payload?.data?.billHeaderResponse?.billNo
            ? response?.payload?.data?.billHeaderResponse?.billNo
            : null,
        });
      }
    } catch (error) {
      console.error("Error in fetching bill:", error);
    }
  };

  useEffect(() => {
    if (
      billItemsList &&
      billItemsList.length > 0 &&
      preAuthClaim?.procedureDropDown
    ) {
      const autoMappedProcedures = mapBillItemsWithProcedures(
        billItemsList || [],
        preAuthClaim?.procedureDropDown,
      );
      const items = autoMappedProcedures?.map((item: any) => ({
        expenseUnit: item?.expenseUnit ?? null,
        expenseCode: item?.expenseCode ?? null,
        admissibleRate: item?.admissibleRate ?? null,
        hospitalRate: item?.hospitalRate ?? null,
        hospitalAmount:
          item?.hospitalRate && item?.expenseUnit
            ? parseFloat(item?.hospitalRate) * parseFloat(item?.expenseUnit)
            : null,
        hospitalRemark: item?.hospitalRemark ?? null,
        id: item?.id ?? null,
        procedureServiceName: item?.procedureServiceName ?? null,
        procedureID: item?.expenseName
          ? JSON.stringify({
              rateType: item?.rateType ?? null,
              expenseType: item?.expenseType ?? null,
              expenseCode: item?.expenseCode ?? null,
              procedureID: item?.procedureID ?? null,
              expenseName: item?.expenseName ?? null,
              // normalRate: item?.normalRate ?? null,
              // admissibleRate: parseInt(item?.admissibleRate)?.toString() ?? null,
            })
          : null,
      }));
      form.setFieldsValue({ items: items?.length > 0 ? items : null });
    }
  }, [billItemsList, preAuthClaim?.procedureDropDown]);

  // function mapBillItemsWithProcedures(billItems: any, procedures: any) {
  //   if (!Array.isArray(billItems) || !Array.isArray(procedures)) {
  //     return [];
  //   }

  //   return (
  //     billItems
  //       ?.filter((item) => item.schemeCode && item.schemeCode.trim() !== "")
  //       ?.map((item) => {
  //         // 2. find matching procedure
  //         const matchedProcedure = procedures.find(
  //           (proc) =>
  //             proc.expenseCode === item.schemeCode ||
  //             proc.procedureID === item.schemeCode
  //         );

  //         if (!matchedProcedure) return null;

  //         const serviceQty = Number(item.serviceQty || 0);
  //         const serviceRate = Number(item.serviceRate || 0);
  //         const schemeRate = Number(item.serviceSchemeRate || 0);

  //         // 3. build required structure
  //         return {
  //           id: null,
  //           rateType: matchedProcedure.rateType,
  //           expenseType: matchedProcedure.expenseType,
  //           expenseCode: matchedProcedure.expenseCode,
  //           expenseName: matchedProcedure.expenseName,
  //           normalRate: matchedProcedure.normalRate ?? null,
  //           expenseUnit: String(serviceQty),
  //           admissibleRate: String(schemeRate),
  //           hospitalRate: String(serviceRate),
  //           hospitalAmount: String(serviceQty * serviceRate),
  //           hospitalRemark: matchedProcedure.hospitalRemark ?? null,
  //           procedureServiceName: item?.serviceName,
  //           procedureID: matchedProcedure.procedureID,
  //         };
  //       })
  //       ?.filter(Boolean)
  //   );
  // }

  function mapBillItemsWithProcedures(billItems: any, procedures: any) {
    if (!Array.isArray(billItems) || !Array.isArray(procedures)) {
      return [];
    }

    const groupedMap = new Map();

    billItems
      ?.filter((item) => item.schemeCode && item.schemeCode.trim() !== "")
      ?.forEach((item) => {
        const matchedProcedure = procedures.find(
          (proc) =>
            proc.expenseCode === item.schemeCode ||
            proc.procedureID === item.schemeCode,
        );

        if (!matchedProcedure) return;

        const serviceQty = Number(item.serviceQty || 0);
        const serviceRate = Number(item.serviceRate || 0);
        const schemeRate = Number(item.serviceSchemeRate || 0);

        const key =
          matchedProcedure.procedureID || matchedProcedure.expenseCode;

        if (groupedMap.has(key)) {
          const existing = groupedMap.get(key);

          const newQty = Number(existing.expenseUnit) + serviceQty;

          existing.expenseUnit = String(newQty);
          existing.hospitalAmount = String(
            newQty * Number(existing.hospitalRate),
          );

          groupedMap.set(key, existing);
        } else {
          groupedMap.set(key, {
            id: null,
            rateType: matchedProcedure.rateType,
            expenseType: matchedProcedure.expenseType,
            expenseCode: matchedProcedure.expenseCode,
            expenseName: matchedProcedure.expenseName,
            normalRate: matchedProcedure.normalRate ?? null,
            expenseUnit: String(serviceQty),
            admissibleRate: String(schemeRate),
            hospitalRate: String(serviceRate),
            hospitalAmount: String(serviceQty * serviceRate),
            hospitalRemark: matchedProcedure.hospitalRemark ?? null,
            procedureServiceName: item?.serviceName,
            procedureID: matchedProcedure.procedureID,
          });
        }
      });

    return Array.from(groupedMap.values());
  }

  return (
    <>
      {isClaimLoading ? (
        <Loader />
      ) : (
        <EchsClaimForm
          claimData={claimData}
          procedureDropDown={preAuthClaim?.procedureDropDown}
          // isEditable={!(claimData?.submitted || claimData?.approved)}
          isEditable={!claimData?.submitted}
          caseClaimSubmissionId={caseClaimId}
          portalSubmission={portalSubmission}
          handleDeleteProcedure={handleDeleteProcedure}
          form={form}
          activeKey={activeKey}
          handleFileChange={handleFileChange}
          handleSaveData={handleSaveData}
          handleOpen={handleOpen}
          preAuth={preAuth}
          loading={loading}
          documentRequestResponseDocuments={documentRequestResponseDocuments}
          documentTypes={documentTypes}
          documentUrls={documentUrls}
          isApproved={isApproved}
          isSaving={isSaving}
          isIntimating={isIntimating}
          handleSaveConfirmationModal={handleSaveConfirmationModal}
          isApprovedInitiated={isApprovedInitiated}
          isSubmitting={isSubmitting}
          caseClaimId={caseClaimId}
          uploadFileList={uploadFileList}
          setIsAmountModal={setIsAmountModal}
          roomChargesOptions={roomChargesOptions}
          consultationChargesOptions={consultationChargesOptions}
          implantListOptions={implantListOptions}
          isFetchingRoomCharges={isFetchingRoomCharges}
          isFetchingConsultationCharges={isFetchingConsultationCharges}
          isFetchingImplantList={isFetchingImplantList}
          billRequiredDocuments={billRequiredDocuments}
          claimExtraDocuments={claimExtraDocuments}
          onSupportingFileSelect={handleBillRequiredFileSelect}
          onViewSupportingDocument={(record: any, documentUrl?: string) => {
            const uploadedDocs =
              record?.documentList?.filter(
                (entry: any) => entry?.documentUrl,
              ) || [];
            handleOpenSupportingDocuments(
              record?.label,
              documentUrl || uploadedDocs[0]?.documentUrl,
            );
          }}
          onDownloadSupportingDocument={handleDownloadDocument}
          onDeleteSupportingDocument={(record: any, documentUrl?: string) => {
            handleBillRequiredDelete(record, documentUrl);
          }}
        />
      )}

      {previewModalVisible &&
        selectedFile &&
        selectedFile?.file &&
        !selectedFiles?.length && (
          <FilePreviewModal
            isVisible={previewModalVisible}
            onClose={() => setPreviewModalVisible(false)}
            fileUrl={previewFile.url}
            fileName={previewFile.name}
            documentType={previewFile.documentType}
            handleUpload={handleUpload}
            showUploadButton={true}
            key={selectedFile?.file?.lastModified || "no-file"}
            isLoading={enhancement?.isLoading}
          />
        )}

      {previewDocument && (
        <PatientDetailsStageDocument
          handleCancel={handleCancelPreview}
          handleDeleteDocument={handleDeleteList}
          open={previewDocument}
          handleDownloadDocument={handleDownloadDocument}
          // handleSetDocumentUrl={handleSetDocumentUrl}
          documents={documentUrls}
          handleDeficiencyModal={handleDeficiencyModalOpen}
          closeDeficiencyModal={closeDeficiencyModal}
          deficiencyModal={deficiencyModal}
          deficiencyQuestions={patients?.deficiencyCheckQuestions}
          setDocumentName={setDocumentName}
          selectedDocument={selectedDocument}
          setUploadedDocumentUrl={setDocumentUrls}
          activeKey={activeKey}
          clickedActiveTab={clickedActiveTab}
          isSubmittedStatus={portalSubmission}
          fetchingDeficiencyData={fetchingDeficiencyData}
          isHospitalBillDischargeSummary={true}
          clickedActiveDocumentUrl={clickedActiveDocumentUrl}
          isDischargePhotoClaim={true}
        />
      )}

      {deficiencyModal && selectedDocument && (
        <DocumentDeficiencyModal
          key={previewDocumentUrl}
          deficiencyModal={deficiencyModal}
          closeDeficiencyModal={() => setDeficiencyModal(false)}
          documentUrl={previewDocumentUrl}
          documentName={documentName}
          handleDownloadDocument={handleDownloadDocument}
          deficiencyQuestions={patients?.deficiencyCheckQuestions}
          selectedDocument={selectedDocument}
          setUploadedDocumentUrl={setDocumentUrls}
        />
      )}

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={title}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={
          isSaving || isApprovedInitiated || isSubmitting || isIntimating
        }
      />

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      {patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        "OP" && patients?.fetchBasicDetails?.patientResponse?.age < 70 ? (
        <BillAmountConfirmationModal
          isModalVisible={isAmountModal}
          handleCancel={() => {
            setIsAmountModal(false);
          }}
          handleOk={() => {
            setIsAmountModal(false);
          }}
          text={
            "Procedure(s) added exceed ₹5000. Additional approval may be required"
          }
          showCloseButton={true}
        />
      ) : (
        <></>
      )}

      <Modal
        title="Document Signature Status"
        visible={showUnsignedDocumentsModal}
        onCancel={() => setShowUnsignedDocumentsModal(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setShowUnsignedDocumentsModal(false)}
          >
            Close
          </Button>,
          <Button
            key="continue"
            type="primary"
            style={{ backgroundColor: "rgb(0, 76, 143)", color: "#fff" }}
            onClick={() => {
              setShowUnsignedDocumentsModal(false);
              if (confirmationState === "intimate") {
                setTitle("Do you want to send Claim for QC ?");
                setConfirmationModal(true);
                setConfirmationState("intimate");
              } else {
                setTitle("Do You want to submit Claim ?");
                setConfirmationModal(true);
                setConfirmationState("submit");
              }
            }}
          >
            Continue Anyway
          </Button>,
        ]}
        centered
        width={700}
      >
        <div style={{ padding: "20px 0" }}>
          <p
            style={{
              fontSize: "16px",
              marginBottom: "20px",
              fontWeight: "bold",
              color: "#d32f2f",
            }}
          >
            {unsignedDocumentsCount} document(s) are NOT digitally signed
          </p>

          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {/* Digitally Signed Documents */}
            {digitallySignedDocuments?.some(
              (doc: any) => doc?.digitallySigned,
            ) && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ color: "#2e7d32", marginBottom: "10px" }}>
                  ✓ Digitally Signed Documents
                </h4>
                {digitallySignedDocuments
                  ?.filter((doc: any) => doc?.digitallySigned)
                  ?.map((doc: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#e8f5e9",
                        marginBottom: "8px",
                        borderRadius: "4px",
                        borderLeft: "4px solid #2e7d32",
                      }}
                    >
                      <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                        ✓ {doc?.documentLabelType || doc?.documentName}
                      </span>
                    </div>
                  ))}
              </div>
            )}

            {/* Non-Digitally Signed Documents */}
            {digitallySignedDocuments?.some(
              (doc: any) => !doc?.digitallySigned,
            ) && (
              <div>
                <h4 style={{ color: "#d32f2f", marginBottom: "10px" }}>
                  ✗ Not Digitally Signed Documents
                </h4>
                {digitallySignedDocuments
                  ?.filter((doc: any) => !doc?.digitallySigned)
                  ?.map((doc: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#ffebee",
                        marginBottom: "8px",
                        borderRadius: "4px",
                        borderLeft: "4px solid #d32f2f",
                      }}
                    >
                      <span style={{ color: "#d32f2f", fontWeight: "500" }}>
                        ✗ {doc?.documentLabelType || doc?.documentName}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <p style={{ color: "#666", fontSize: "14px", marginTop: "20px" }}>
            Please ensure all documents are digitally signed before initiating
            the Claim.
          </p>
        </div>
      </Modal>

      <Modal
        open={isFetchingDigitallySignedDocs}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={820}
      >
        <div
          style={{
            textAlign: "center",
            padding: "56px 32px",
            backgroundColor: "#ffffff",
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 48, color: "rgb(0, 76, 143)" }}
                spin
              />
            }
          />

          <Title
            level={4}
            style={{
              marginTop: 32,
              marginBottom: 12,
              color: "rgb(0, 76, 143)",
              fontWeight: 600,
            }}
          >
            Verifying Digital Signature
          </Title>

          <Text
            style={{
              color: "rgba(0, 0, 0, 0.65)",
              fontSize: 14,
              lineHeight: 1.6,
              maxWidth: 800,
              display: "inline-block",
            }}
          >
            We are currently validating the digitally signed document. Please
            keep this page open and avoid refreshing until the process is
            complete.
          </Text>
        </div>
      </Modal>

      <Modal
        open={isAllDigitallySignedModal}
        footer={null}
        closable={true}
        maskClosable={false}
        centered
        width={520}
        onCancel={() => setIsAllDigitallySignedModal(false)}
      >
        <div style={{ textAlign: "center", padding: "24px 12px" }}>
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 56 }} />

          <Title level={4} style={{ marginTop: 24 }}>
            All Documents Are Digitally Signed
          </Title>

          <Text type="secondary">
            The digital signing process has been completed successfully. You may
            now continue to the next step.
          </Text>

          <Space size="middle" style={{ marginTop: 32 }}>
            <Button onClick={() => setIsAllDigitallySignedModal(false)}>
              Close
            </Button>

            <Button
              type="primary"
              size="large"
              style={{ backgroundColor: "rgb(0, 76, 143)", color: "#fff" }}
              onClick={() => {
                try {
                  setIsAllDigitallySignedModal(false);
                  if (confirmationState === "intimate") {
                    setTitle("Do you want to send Claim for QC ?");
                    setConfirmationModal(true);
                    setConfirmationState("intimate");
                  } else {
                    setTitle("Do You want to submit Claim ?");
                    setConfirmationModal(true);
                    setConfirmationState("submit");
                  }
                } catch (err: any) {
                  if (err?.errorFields) {
                    errorMessage(
                      "Please fill all required fields before submitting",
                    );
                  } else {
                    errorMessage("Please try again later");
                  }
                }
              }}
            >
              Continue
            </Button>
          </Space>
        </div>
      </Modal>

      <SupportingDocumentViewerModal
        open={supportingPreviewOpen}
        documents={supportingDocumentUrls}
        clickedActiveTab={supportingClickedActiveTab}
        clickedActiveDocumentUrl={supportingClickedActiveDocumentUrl}
        portalSubmission={portalSubmission}
        onCancel={handleCancelSupportingPreview}
        onDownload={handleDownloadDocument}
        onDelete={handleSupportingDeleteList}
      />

      {billRequiredPreviewFile.file && (
        <FilePreviewModal
          isVisible={billRequiredPreviewVisible}
          onClose={resetBillRequiredPreview}
          fileUrl={billRequiredPreviewFile.url}
          fileName={billRequiredPreviewFile.name}
          documentType={String(
            billRequiredUploadContext?.doc?.labelId ??
              billRequiredUploadContext?.doc?.id ??
              "",
          )}
          handleUpload={handleBillRequiredPreviewUpload}
          showUploadButton
          key={billRequiredPreviewFile.file.lastModified}
          isLoading={billRequiredUploading || enhancement?.isLoading}
        />
      )}
    </>
  );
};

export default ClaimEchsContainer;
